/**
 * FamilyCalendarPage — /family/:slug/calendar
 *
 * Unified monthly scroll view of birthdays + anniversaries + memorials + events.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Cake, Heart, Flame, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { http } from "@/lib/api";

const P = {
  ink: "#431a04",
  inkSoft: "#8a5a2e",
  saffron: "#C2410C",
  saffronBg: "#FFF7ED",
  parchment: "#FFFBF4",
  line: "rgba(194,65,12,0.14)",
};

const KIND_META = {
  birthday:    { Icon: Cake,   color: "#C2410C", label: "Birthday" },
  anniversary: { Icon: Heart,  color: "#B23A3A", label: "Anniversary" },
  remembrance: { Icon: Flame,  color: "#7A3A1A", label: "Remembrance" },
  event:       { Icon: CalendarIcon, color: "#2D6B4F", label: "Event" },
};

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function monthKey(iso) {
  if (!iso || iso.length < 7) return "";
  return iso.slice(0, 7); // YYYY-MM
}

function dayNum(iso) {
  if (!iso || iso.length < 10) return "";
  return parseInt(iso.slice(8, 10), 10);
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
}

function daysFromToday(iso) {
  if (!iso) return 9999;
  try {
    const t = new Date(iso + "T00:00:00Z").getTime();
    const now = new Date(); now.setUTCHours(0,0,0,0);
    return Math.round((t - now.getTime()) / 86400000);
  } catch { return 9999; }
}

export default function FamilyCalendarPage() {
  const { slug } = useParams();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-indexed
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [treeName, setTreeName] = useState("");

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    http.get(`/family-trees/${slug}/calendar?year=${year}&month=${month}`)
      .then((r) => {
        if (cancel) return;
        // Backend returns {entries, range, count}. Normalise each entry so
        // the UI can be kind-agnostic. Birth-remembrance for deceased
        // people is folded into "remembrance" category.
        const entries = (r.data?.entries || []).map((e) => {
          const kind = e.kind === "memorial_day" || e.kind === "birth_remembrance"
            ? "remembrance"
            : e.kind;
          // Derive member_name/spouse_name from title when needed.
          let member_name = e.member_name;
          let spouse_name = e.spouse_name;
          if (!member_name && e.title) {
            if (kind === "anniversary" && e.title.includes(" & ")) {
              const [a, rest] = e.title.split(" & ");
              member_name = a.trim();
              spouse_name = rest.replace(/ anniversary$/i, "").trim();
            } else if (kind === "birthday") {
              member_name = e.title.replace(/'s birthday.*/i, "").trim();
            } else if (kind === "remembrance") {
              member_name = e.title.replace(/^In memory of /i, "").trim();
            }
          }
          return { ...e, kind, member_name, spouse_name };
        });
        setItems(entries);
      })
      .catch(() => { if (!cancel) setItems([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    http.get(`/family-trees/${slug}`).then((t) => {
      if (!cancel && t.data?.name) setTreeName(t.data.name);
    }).catch(() => {});
    return () => { cancel = true; };
  }, [slug, year, month]);

  // Group items by month for the scroll view (show current + next 2 months)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const mk = monthKey(it.date);
      if (!mk) continue;
      if (!map.has(mk)) map.set(mk, []);
      map.get(mk).push(it);
    }
    // Sort months, keep only current month and 2 forward.
    const currentKey = `${year}-${String(month).padStart(2,"0")}`;
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    const next2Month = nextMonth === 12 ? 1 : nextMonth + 1;
    const next2Year = nextMonth === 12 ? nextYear + 1 : nextYear;
    const keys = [
      currentKey,
      `${nextYear}-${String(nextMonth).padStart(2,"0")}`,
      `${next2Year}-${String(next2Month).padStart(2,"0")}`,
    ];
    return keys.map((k) => [k, (map.get(k) || []).sort((a, b) => (a.date || "").localeCompare(b.date || ""))]);
  }, [items, year, month]);

  const canonical = typeof window !== "undefined" ? `${window.location.origin}/family/${slug}/calendar` : "";

  const prev = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };
  const next = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10" data-testid="family-calendar-page">
      <Helmet>
        <title>{`${treeName ? `${treeName} Family Calendar` : "Family Calendar"} · IndianFamilyTree`}</title>
        <meta name="description" content="Birthdays, anniversaries, remembrances and family events — all in one place." />
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <div className="mb-6">
        <Link to={`/family/${slug}`} className="text-xs font-semibold uppercase tracking-widest" style={{ color: P.saffron }}>
          ← Back to {treeName || "the family tree"}
        </Link>
        <h1 className="font-display text-3xl lg:text-4xl mt-3" style={{ color: P.ink }}>
          Family Calendar
        </h1>
        <p className="text-sm lg:text-base mt-2" style={{ color: P.inkSoft }}>
          Every birthday, anniversary and remembrance — one place to never miss a date that matters.
        </p>
      </div>

      <div
        className="rounded-2xl flex items-center justify-between px-4 py-3 mb-5"
        style={{ background: P.saffronBg, border: `1px solid ${P.line}` }}
      >
        <button
          onClick={prev}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white transition-colors"
          aria-label="Previous month"
          data-testid="fc-prev"
          style={{ color: P.saffron }}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-sm font-semibold" style={{ color: P.ink }} data-testid="fc-current-month">
          {MONTH_NAMES[month - 1]} {year}
        </div>
        <button
          onClick={next}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white transition-colors"
          aria-label="Next month"
          data-testid="fc-next"
          style={{ color: P.saffron }}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-3 py-10 text-sm" style={{ color: P.inkSoft }}>
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: P.saffron }} />
          Loading the family calendar…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div
          className="rounded-2xl p-6 text-center text-sm"
          style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.inkSoft }}
        >
          No dates recorded yet. Add birthdays and anniversaries to family profiles to see them here.
        </div>
      )}

      {!loading && grouped.map(([mk, rows]) => {
        if (rows.length === 0) return null;
        const [yy, mm] = mk.split("-");
        return (
          <section key={mk} data-testid={`fc-month-${mk}`} className="mb-8">
            <h2 className="font-display text-xl lg:text-2xl mb-3" style={{ color: P.ink }}>
              {MONTH_NAMES[parseInt(mm, 10) - 1]} {yy}
            </h2>
            <ol className="space-y-2">
              {rows.map((it, i) => {
                const meta = KIND_META[it.kind] || KIND_META.event;
                const Icon = meta.Icon;
                const dfromnow = daysFromToday(it.date);
                return (
                  <li
                    key={`${mk}-${i}-${it.member_id || it.event_id || it.date}`}
                    className="flex items-center gap-3 rounded-2xl px-4 py-3"
                    style={{ background: "#fff", border: `1px solid ${P.line}` }}
                    data-testid={`fc-item-${it.kind}`}
                  >
                    <div
                      className="shrink-0 w-11 h-11 rounded-xl flex flex-col items-center justify-center"
                      style={{ background: P.saffronBg, color: meta.color }}
                    >
                      <div className="text-[10px] font-semibold uppercase leading-none mt-0.5">
                        {(it.date || "").slice(5, 7) && MONTH_NAMES[parseInt((it.date || "").slice(5,7),10)-1]?.slice(0,3)}
                      </div>
                      <div className="text-base font-bold leading-none mt-0.5">{dayNum(it.date)}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                        <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: meta.color }}>
                          {meta.label}
                          {it.years != null && it.years > 0 ? ` · ${it.years} yrs` : ""}
                          {it.age != null && it.age > 0 && it.kind === "birthday" ? ` · ${it.age}` : ""}
                        </span>
                      </div>
                      <div className="text-sm font-medium truncate" style={{ color: P.ink }}>
                        {it.kind === "anniversary"
                          ? `${it.member_name}${it.spouse_name ? " & " + it.spouse_name : ""}`
                          : it.kind === "event"
                            ? it.title
                            : it.member_name}
                      </div>
                      {it.location && (
                        <div className="text-xs truncate" style={{ color: P.inkSoft }}>{it.location}</div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xs" style={{ color: P.inkSoft }}>{formatDate(it.date)}</div>
                      {dfromnow >= 0 && dfromnow <= 30 && (
                        <div className="text-[10px] font-semibold" style={{ color: P.saffron }}>
                          {dfromnow === 0 ? "Today" : dfromnow === 1 ? "Tomorrow" : `in ${dfromnow} days`}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        );
      })}
    </div>
  );
}
